import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [fullName, setFullName] = useState('');
  const [institutionName, setInstitutionName] = useState('');
  const [academicType, setAcademicType] = useState('');
  const [orcidId, setOrcidId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const navigate = useNavigate();

  const { setAuth } = useAuth();

  const handleEmailVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = { email };

    try {
      const response = await fetch(
        'https://academichub-backend.onrender.com/auth/signup/verifyemail',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (response.ok) {
        console.log('Verification email sent');
        setIsModalOpen(true); // Open the modal for entering the code
        setInstitutionName(data.institutionName);
      } else {
        console.error('Failed to send verification email');
      }
    } catch (error) {
      console.error('Error during email verification:', error);
    }
  };

  // Handle code input for the 6-digit verification
  const handleCodeChange = (index: number, value: string) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-focus next input if a digit is entered
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  // Handle code verification
  const handleCodeVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    const verificationCode = code.join('');
    const payload = { email, code: verificationCode };

    try {
      const response = await fetch(
        'https://academichub-backend.onrender.com/auth/signup/verifycode',
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        console.log('Code verified successfully');
        setIsModalOpen(false);
        setIsVerified(true);
      } else {
        console.error('Code verification failed');
      }
    } catch (error) {
      console.error('Error during code verification:', error);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      console.error('Passwords do not match');
      return;
    }

    const payload = {
      fullName,
      universityEmail: email,
      institutionName,
      academicType,
      orcidId,
      password,
    };
    console.log(payload);
    try {
      const response = await fetch('https://academichub-backend.onrender.com/auth/signup', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Signup successful:', data);
        setAuth(true);
        // Redirect using React Router
        navigate('/');
      } else {
        const data = await response.json();
        alert(`Signup failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Error during signup:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center w-full">
      <div className="p-8 rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-center">
          <img src="/assets/logo.svg" alt="Logo" className="w-52 h-auto" />
        </div>
        <h1 className="text-5xl font-semibold text-center text-white mb-6">Signup</h1>

        {/* Step 1: Email Input */}
        {!isVerified && !isModalOpen && (
          <>
            <p className="text-center text-white mb-6">
              Welcome to AcademicHub. Do you{' '}
              <button onClick={() => navigate('/login')} className="text-teal-400 hover:underline">
                already have an account?
              </button>
            </p>
            <form onSubmit={handleEmailVerification} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  University Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-teal-500 text-white py-2 px-4 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
              >
                <span className="flex items-center justify-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Continue with email
                </span>
              </button>{' '}
            </form>
          </>
        )}

        {/* Step 2: Verification Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Verification Code</h2>
              <p className="text-gray-600 mb-6">
                Enter the 6-digit code we&apos;ve emailed to your institution email.
              </p>
              <form onSubmit={handleCodeVerification} className="space-y-6">
                <div className="flex justify-between">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      id={`code-${index}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleCodeChange(index, e.target.value)}
                      className="w-10 h-10 text-center text-lg bg-gray-200 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  ))}
                </div>
                <button
                  type="submit"
                  className="w-full bg-teal-500 text-white py-2 px-4 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                >
                  <span className="flex items-center justify-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Verify account
                  </span>
                </button>{' '}
              </form>
            </div>
          </div>
        )}

        {/* Step 3: Full Signup Form */}
        {isVerified && (
          <form onSubmit={handleSignup} className="space-y-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-300">
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>
            <div>
              <label htmlFor="universityEmail" className="block text-sm font-medium text-gray-300">
                University Email <span className="text-gray-400">(verified)</span>
              </label>
              <input
                type="email"
                id="universityEmail"
                value={email}
                readOnly
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>
            <div>
              <label htmlFor="institutionName" className="block text-sm font-medium text-gray-300">
                Institution Name
              </label>
              <input
                type="text"
                id="institutionName"
                value={institutionName}
                readOnly
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>
            <div>
              <label htmlFor="academicType" className="block text-sm font-medium text-gray-300">
                Type of Academic
              </label>
              <select
                id="academicType"
                value={academicType}
                onChange={e => setAcademicType(e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
              >
                <option value="">Select...</option>
                <option value="student">Undergraduate Researcher</option>
                <option value="researcher">Postgraduate Researcher</option>
                <option value="professor">Professor</option>
              </select>
            </div>
            <div>
              <label htmlFor="orcidId" className="block text-sm font-medium text-gray-300">
                ORCID ID <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                id="orcidId"
                value={orcidId}
                onChange={e => setOrcidId(e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-teal-500 text-white py-2 px-4 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
            >
              <span className="flex items-center justify-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Continue
              </span>
            </button>{' '}
          </form>
        )}
      </div>
    </div>
  );
};

export default Signup;
